import {Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, WritableSignal} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {ConfirmDialog} from "@components/dialogs/confirm-dialog/confirm-dialog";
import {IconBoldTrash} from "@components/icons/icon-bold-trash/icon-bold-trash";
import {IconCloseSquareComponent} from "@components/icons/icon-close-square/icon-close-square.component";
import {IconSaveComponent} from "@components/icons/icon-save/icon-save.component";
import {EnumResponse} from "@core/models/enum-response";
import {Notificacao} from "@core/models/notificacoes";
import {EnumsService} from "@core/services/enums.service";
import {NotificacaoService} from "@core/services/notificacao.service";
import {CurrencyMaxDirective} from "@shared/directives/currency-max.directive";
import {HasPermissionDirective} from "@shared/directives/has-permission.directive";
import {DateUtils} from "@shared/utils/date.utils";
import {StringUtils} from "@shared/utils/string.utils";
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from "primeng/accordion";
import {MessageService} from "primeng/api";
import {Button} from "primeng/button";
import {Card} from "primeng/card";
import {AbrirSei} from '@components/commons/abrir-sei/abrir-sei';
import {DatePicker} from "primeng/datepicker";
import {Fluid} from "primeng/fluid";
import {InputNumber} from "primeng/inputnumber";
import {InputText} from "primeng/inputtext";
import {Message} from "primeng/message";
import {Select} from "primeng/select";
import {Textarea} from "primeng/textarea";
import {Subscription} from "rxjs";
import {InputMask} from 'primeng/inputmask';

@Component({
  selector: 'app-add-edit-notificacao',
  imports: [
    AbrirSei,
    Button,
    Card,
    IconBoldTrash,
    IconCloseSquareComponent,
    IconSaveComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    ReactiveFormsModule,
    Select,
    Message,
    DatePicker,
    Fluid,
    InputText,
    Textarea,
    ConfirmDialog,
    CurrencyMaxDirective,
    HasPermissionDirective,
    InputNumber,
    InputMask
  ],
  templateUrl: './add-edit-notificacao.html',
  styleUrl: './add-edit-notificacao.css'
})
export class AddEditNotificacao implements OnInit, OnChanges, OnDestroy {
  dadosIniciaisForm!: FormGroup;
  processosForm!: FormGroup;
  infracaoForm!: FormGroup;
  @Input() id: number | null = null;

  valorExcedeMax: boolean = false;

  showConfirmSaveDialog: boolean | WritableSignal<boolean> = false;
  showCancelSaveDialog: boolean | WritableSignal<boolean> = false;
  showConfirmEditDialog: boolean | WritableSignal<boolean> = false;
  showCancelEditDialog: boolean | WritableSignal<boolean> = false;
  showConfirmProcessoSEIFormatDialog: boolean | WritableSignal<boolean> = false;
  showConfirmDeleteDialog: boolean | WritableSignal<boolean> = false;

  isEdit = false;
  protected extraColumns: Array<{ id: string; label: string; type: 'text' | 'number' | 'date' }> = [];
  private extraData: Record<string, Record<string, string>> = {};
  protected superintendenciaOptions: EnumResponse[] = [];
  protected diretoriaOptions: EnumResponse[] = [];
  protected concessionariaOptions: EnumResponse[] = [];
  protected motivoOptions: EnumResponse[] = [];
  protected statusOptions: EnumResponse[] = [];
  protected faseOptions: EnumResponse[] = [];
  protected tipoTapOptions: EnumResponse[] = [];
  protected situacaoTapOptions: EnumResponse[] = [];
  protected grupoOptions: EnumResponse[] = [];
  protected nivelOptions: EnumResponse[] = [];
  protected cursoOptions: EnumResponse[] = [];
  protected showInputMaskProcesso = true;
  private subscriptions: Subscription[] = [];
  private service = inject(NotificacaoService);
  private messageService = inject(MessageService);
  private enumsService = inject(EnumsService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  get allFormsValid(): boolean {
    return this.dadosIniciaisForm.valid && this.processosForm.valid && this.infracaoForm.valid;
  }

  objectKeys = (obj: any) => Object.keys(obj || {});

  ngOnInit() {
    this.initForms();
    this.setFormsValidators();
    this.verifyAndGetIdFromRoute();
    this.enumsService.initEnums().then(() => this.setEnums());
    if (this.id) {
      this.getNotificacaoById(this.id);
    }
    if (this.isEdit) {
      this.loadExtraColumns();
    }
    // "Abrir no SisNot" do SEI sem notificação ainda: cadastro com o processo preenchido
    const processo = this.route.snapshot.queryParamMap.get('processo');
    if (!this.isEdit && processo && /^\d{3}\.\d{7,8}\/\d{4}-\d{2}$/.test(processo)) {
      this.processosForm.patchValue({processo});
    }
  }

  protected getExtraValue(colId: string): string {
    if (this.id == null) return '';
    return this.extraData[String(this.id)]?.[colId] ?? '';
  }

  protected setExtraValue(colId: string, value: string): void {
    if (this.id == null) return;
    if (!this.extraData[String(this.id)]) this.extraData[String(this.id)] = {};
    this.extraData[String(this.id)][colId] = value;
    localStorage.setItem('notificacoes_extra_data', JSON.stringify(this.extraData));
  }

  private loadExtraColumns(): void {
    try {
      const savedColumns = localStorage.getItem('notificacoes_extra_columns');
      if (savedColumns) this.extraColumns = JSON.parse(savedColumns);
      const savedData = localStorage.getItem('notificacoes_extra_data');
      if (savedData) this.extraData = JSON.parse(savedData);
    } catch {
    }
  }

  verifyAndGetIdFromRoute() {
    const params = this.route.snapshot.paramMap;
    if (params.has('id')) {
      console.info('[AddEditNotificacao] route param id founded. Activated edit mode.');

      this.id = Number(params.get('id')) ?? null;
      this.isEdit = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.reset();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  initForms(): void {
    this.dadosIniciaisForm = this.fb.group({
      superintendencia: [null, Validators.required], // select
      diretoria: [null], // select
      gerencia: [''], // text
      cff: [''], // numero mask=cff
      numeroNotificacao: [null], // numero
      dataNotificacao: [null, Validators.required], // date
      concessionaria: [null, Validators.required], // select
    });

    this.processosForm = this.fb.group({
      categoria: [null], // text
      processo: [null, Validators.required], // (50 dígitos)
      descricao: [null], // text
      motivo: [null, Validators.required], // select
      status: [null, Validators.required], // select
      fase: [null, Validators.required], // select
      valorP0: [null], // currency
      valorAtual: [null], // currency
      numeroTap: [null], // numero
      tipoTap: [null], // select
      dataTap: [null], // date dd/mm/yyyy
      valorTap: [null], // currency
      situacaoTap: [null], // select
      andamento: [null, Validators.required], // date dd/mm/yy
      localizacao: [null], // text
      infracoes: [null], // number
      grupo: [null], // select
      nivel: [null], // select
      avanco: [null], // numero mask=percent XXX,xx%
      judicial: [null], // text
    });

    this.infracaoForm = this.fb.group({
      rodovia: [null], // numero mask=SP-xxx
      observacao: [null], // text
      curso: [null, Validators.required] // select
    });
  }

  save() {
    if (!this.allFormsValid) {
      this.showInvalidForm();
      return;
    }
    const payload = this.getPayload();

    if (this.isEdit && this.id) {
      this.service.put(this.id, payload).subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo!',
            detail: 'Notificação foi alterada.'
          });
          this.reset();
          this.router.navigate(['/controle-notificacoes']).then(() => {
            console.info('[AddEditNotificacao] Redirected to /controle-notificacoes');
          });
        },
        error: (error) => {
          console.error('[AddEditNotificacao] Erro ao editar notificação: ', error?.error?.errors);
          this.messageService.add({
            severity: 'error',
            summary: 'Erro!',
            detail: error?.error?.message || 'Erro desconhecido ao alterar notificação.'
          });
        }
      });
      return;
    }

    this.service.create(payload).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo!',
          detail: 'Notificação foi criada.'
        });
        this.reset();
        this.router.navigate(['/controle-notificacoes']).then(() => {
          console.info('[AddEditNotificacao] Redirected to /controle-notificacoes');
        });
      },
      error: (error) => {
        console.error('[AddEditNotificacao] Erro ao criar notificação: ', error?.error?.errors);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: error?.error?.message || 'Erro desconhecido ao criar notificação.'
        });
      }
    })
  }

  getNotificacaoById(id: number) {
    if (id) {
      this.service.getById(id).subscribe({
        next: (response: Notificacao) => {
          this.patchFormFromNotificacao(response);
        },
        error: error => {
          console.error('[AddEditNotificacao] Erro ao buscar dados da notificação pelo id fornecido: ', error?.error?.errors);
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: error?.error?.message || 'Erro ao carregar os dados da notificação.',
          })
        }
      })
    }
  }


  patchFormFromNotificacao(data: Notificacao) {
    if (data) {
      this.dadosIniciaisForm.patchValue({
        superintendencia: this.enumsService.getValueFromEnum(data.superintendencia),
        diretoria: this.enumsService.getValueFromEnum(data.diretoria),
        gerencia: data.gerencia || '',
        cff: data.cff || '',
        numeroNotificacao: data.numeroNotificacao || '',
        dataNotificacao: DateUtils.parseIsoDate(data.dataNotificacao || ''),
        concessionaria: this.enumsService.getValueFromEnum(data.concessionaria),
      });
      const processo = data.processo;
      this.showInputMaskProcesso = this.checkProcessoMask(processo);
      this.processosForm.patchValue({
        categoria: data.categoria || '',
        processo: processo || '',
        descricao: data.descricao || '',
        motivo: this.enumsService.getValueFromEnum(data.motivo),
        status: this.enumsService.getValueFromEnum(data.status),
        fase: this.enumsService.getValueFromEnum(data.fase),
        valorP0: data.valorP0 || 0,
        valorAtual: data.valorAtual || 0,
        numeroTap: data.numeroTap || '',
        tipoTap: this.enumsService.getValueFromEnum(data.tipoTap),
        dataTap: DateUtils.parseIsoDate(data.dataTap || ''),
        valorTap: data.valorTap || 0,
        situacaoTap: this.enumsService.getValueFromEnum(data.situacaoTap),
        andamento: DateUtils.parseIsoDate(data.andamento || ''),
        localizacao: data.localizacao || '',
        infracoes: data.infracoes || 0,
        grupo: this.enumsService.getValueFromEnum(data.grupo),
        nivel: this.enumsService.getValueFromEnum(data.nivel),
        avanco: data.avanco || 0,
        judicial: data.judicial || '',
      });

      this.infracaoForm.patchValue({
        rodovia: data.rodovia || '',
        observacao: data.observacao || '',
        curso: this.enumsService.getValueFromEnum(data.curso),
      });
    }
  }

  onClickCancel() {
    if (this.isEdit) {
      this.showCancelEditDialog = true;
      return;
    }
    this.showCancelSaveDialog = true;
  }

  onClickSave() {
    if (!this.allFormsValid) {
      this.showInvalidForm();
      return;
    }
    const invalidMaskProcessoSEI = !this.checkProcessoMask(this.processosForm.get('processo')?.value)
    if (invalidMaskProcessoSEI) {
      this.showConfirmProcessoSEIFormatDialog = true;
      return;
    }
    this.openConfirmSaveDialog();
  }

  openConfirmSaveDialog() {
    if (this.isEdit) {
      this.showConfirmEditDialog = true;
      return;
    }
    this.showConfirmSaveDialog = true;
  }

  onClickDelete() {
    if (!this.id) return;
    this.showConfirmDeleteDialog = true;
  }

  delete() {
    if (!this.id) return;
    this.service.delete(this.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Notificação foi excluída.'
        });
        this.showConfirmDeleteDialog = false;
        this.router.navigate(['/controle-notificacoes']).then(() => {
          console.info('[AddEditNotificacao] Redirected to /controle-notificacoes');
        });
      },
      error: (error) => {
        console.error('[AddEditNotificacao] Erro ao excluir notificação: ', error?.error?.errors);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: error?.error?.message || 'Erro desconhecido ao excluir notificação.'
        });
        this.showConfirmDeleteDialog = false;
      }
    });
  }

  onCancelDelete() {
    this.showConfirmDeleteDialog = false;
    this.messageService.add({
      severity: 'warn',
      summary: 'Atenção!',
      detail: 'Nenhuma notificação foi excluída.'
    });
  }

  showInvalidForm() {
    if (!this.allFormsValid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Preencha os campos obrigatórios.'
      });
      this.dadosIniciaisForm.markAllAsTouched();
      this.processosForm.markAllAsTouched();
      this.infracaoForm.markAllAsTouched();
      return;
    }
  }

  setFormsValidators(): void {
    const statusControl = this.processosForm.get('status')!;
    const tipoTapControl = this.processosForm.get('tipoTap')!;
    const faseControl = this.processosForm.get('fase')!;
    const dataTapControl = this.processosForm.get('dataTap')!;
    const valorTapControl = this.processosForm.get('valorTap')!;
    const situacaoTapControl = this.processosForm.get('situacaoTap')!;
    const judicialControl = this.processosForm.get('judicial')!;

    dataTapControl.setValidators([this.dataTapValidator(statusControl)]);
    valorTapControl.setValidators([this.valorTapValidator(tipoTapControl, faseControl)]);
    tipoTapControl.setValidators([this.tipoTapValidator(faseControl)]);
    situacaoTapControl.setValidators([this.situacaoTapValidator(statusControl)]);
    judicialControl.setValidators([this.judicialValidator(faseControl)]);

    dataTapControl.updateValueAndValidity({emitEvent: false});
    valorTapControl.updateValueAndValidity({emitEvent: false});
    tipoTapControl.updateValueAndValidity({emitEvent: false});
    situacaoTapControl.updateValueAndValidity({emitEvent: false});
    judicialControl.updateValueAndValidity({emitEvent: false});

    this.subscriptions.push(
      statusControl.valueChanges.subscribe(() => {
        dataTapControl.updateValueAndValidity();
        situacaoTapControl.updateValueAndValidity();
      }),
      tipoTapControl.valueChanges.subscribe(() => {
        valorTapControl.updateValueAndValidity();
      }),
      faseControl.valueChanges.subscribe(() => {
        tipoTapControl.updateValueAndValidity();
        valorTapControl.updateValueAndValidity();
        judicialControl.updateValueAndValidity();
      })
    );
  }

  isFormControlInvalid(form: FormGroup, controlName: string) {
    const control = form.get(controlName);
    return control?.touched && control?.invalid;
  }

  reset() {
    this.resetForm();
    this.resetDialogs();
    this.id = null;
  }

  resetAndGoBack(mode: 'create' | 'edit') {
    this.reset();
    this.router.navigate(['/controle-notificacoes']).then(() => {
      console.info('[AddEditNotificacao] Redirected to /controle-notificacoes');
    });

    const messages = {
      create: 'Nenhuma notificação foi criada.',
      edit: 'Nenhuma notificação foi alterada.'
    };

    this.messageService.add({
      severity: 'warn',
      summary: 'Atenção!',
      detail: messages[mode]
    });
  }

  resetForm() {
    this.dadosIniciaisForm.reset();
    this.processosForm.reset();
    this.infracaoForm.reset();
  }

  resetDialogs() {
    this.showConfirmSaveDialog = false;
    this.showCancelSaveDialog = false;
    this.showConfirmEditDialog = false;
    this.showCancelEditDialog = false;
    this.showConfirmDeleteDialog = false;
  }

  processoSPSPValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;
      return StringUtils.validateProcessoSPSPMask(value) ? null : {invalidFormat: true};
    };
  }

  // HU002v1.RN0012: se status é igual a TAP, dataTap não pode ser vazia
  dataTapValidator(statusControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const statusId = statusControl.value;

      if (statusId === 7 && !control.value) {
        control.markAsTouched();
        return {
          requiredWhenTap: 'Se o Status é TAP, Data TAP não pode ser vazia'
        };
      }

      return null;
    };
  }

  // HU002v1.RN0014: Se TIPO TAP é ADVERTÊNCIA, VALOR TAP não pode ter valor
  valorTapValidator(tipoTapControl: AbstractControl, faseControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const tipoTapId = tipoTapControl.value;
      const faseId = faseControl.value;

      // converte BRL para número seguro
      const valor = Number(this.brlToDecimal(control.value?.toString() || '0'));

      // MULTA + TAP: obrigatório e maior que zero
      if (tipoTapId === 2 && faseId === 12 && valor <= 0) {
        control.markAsTouched();
        return {requiredWhenMultaTap: 'Valor TAP é obrigatório e deve ser maior que zero quando Tipo TAP é "MULTA" e Fase é "TAP"'};
      }

      // ADVERTÊNCIA: deve estar vazio ou zero
      if (tipoTapId === 1 && valor > 0) {
        control.markAsTouched();
        return {mustBeEmpty: 'Valor TAP deve estar vazio quando Tipo TAP é "ADVERTÊNCIA"'};
      }

      return null;
    };
  }

  // HU002v1.RN0013: Se o TIPO TAP é MULTA e FASE é TAP, VALOR TAP não pode ser vazia

  // HU002v1.RN0015: Se a FASE é TAP, TIPO TAP não pode ser vazio
  tipoTapValidator(faseControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const faseId = faseControl.value; // será um número

      if (faseId === 12 && !control.value) {
        control.markAsTouched();
        return {
          requiredWhenTap: 'Se a FASE é TAP, TIPO TAP não pode ser vazio.'
        };
      }
      return null;
    };
  }

  // HU002v1.RN0017: Quando o STATUS for TAP, SITUAÇÃO TAP não pode ser vazio
  situacaoTapValidator(statusControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (statusControl.value === 'TAP' && !control.value) {
        control.markAsTouched();
        return {requiredWhenStatusTap: 'Situação TAP é obrigatória quando Status é "TAP"'};
      }
      return null;
    };
  }

  // HU002v4.RN005: Quando FASE for JUD, então JUDICIAL não pode ser vazio.
  judicialValidator(faseControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const faseId = faseControl.value;

      if (faseId === 11 && !control.value) {
        control.markAsTouched();
        return {
          requiredWhenTap: 'Judicial é obrigatório quando a Fase é "JUD"'
        };
      }

      return null;
    };
  }

  getPayload(): Notificacao {
    let payload: Notificacao = {
      ...this.dadosIniciaisForm.value,
      ...this.processosForm.value,
      ...this.infracaoForm.value
    };

    // Converte os campos currency para string decimal segura
    payload.valorP0 = Number(this.brlToDecimal(payload.valorP0?.toString() || '0'));
    payload.valorAtual = Number(this.brlToDecimal(payload.valorAtual?.toString() || '0'));
    payload.valorTap = Number(this.brlToDecimal(payload.valorTap?.toString() || '0'));

    /* enums values que o backend espera o objeto completo */
    payload.superintendencia = this.enumsService.getObjectFromValue(this.enumsService.superintendencias, payload.superintendencia);
    payload.concessionaria = this.enumsService.getObjectFromValue(this.enumsService.concessionarias, payload.concessionaria);
    payload.motivo = this.enumsService.getObjectFromValue(this.enumsService.motivos, payload.motivo);
    payload.status = this.enumsService.getObjectFromValue(this.enumsService.status, payload.status);
    payload.fase = this.enumsService.getObjectFromValue(this.enumsService.fases, payload.fase);
    payload.tipoTap = this.enumsService.getObjectFromValue(this.enumsService.tiposTap, payload.tipoTap);
    payload.situacaoTap = this.enumsService.getObjectFromValue(this.enumsService.situacoesTap, payload.situacaoTap);
    payload.curso = this.enumsService.getObjectFromValue(this.enumsService.cursos, payload.curso);
    payload.diretoria = this.enumsService.getObjectFromValue(this.enumsService.diretorias, payload.diretoria);
    payload.grupo = this.enumsService.getObjectFromValue(this.enumsService.grupos, payload.grupo);
    payload.nivel = this.enumsService.getObjectFromValue(this.enumsService.niveis, payload.nivel);

    /* datas que o backend espera string yyyy-MM-dd */
    payload.dataTap = DateUtils.toIsoDateOnly(payload.dataTap);
    payload.dataNotificacao = DateUtils.toIsoDateOnly(payload.dataNotificacao);
    payload.andamento = DateUtils.toIsoDateOnly(payload.andamento)
    return payload;
  }

  onAvancoFocus(event: any) {
    // Remove zeros à direita ao focar (edição)
    const value = this.processosForm.get('avanco')?.value;
    if (value != null) {
      const newValue = parseFloat(value).toString();
      this.processosForm.get('avanco')?.setValue(newValue);
    }
  }

  onAvancoInput(event: any) {
    // Garante que o valor não passe de 100
    const value = event.value;
    if (value > 100) {
      this.processosForm.get('avanco')?.setValue(100);
    }
  }

  onAvancoBlur(event: any) {
    // Ao sair do campo, formata com duas casas decimais
    const value = this.processosForm.get('avanco')?.value;
    if (value != null && value !== '') {
      const num = parseFloat(value);
      const formatted = num.toFixed(2);
      this.processosForm.get('avanco')?.setValue(formatted);
    }
  }

  onInfracoesInput(event: any) {
    const control = this.processosForm.get('infracoes');
    const value = event?.value;

    if (value == null || value === '') return;

    const max = 9007199254740991;

    // Impede valores acima do limite do int64
    if (value > max) {
      control?.setValue(max, {emitEvent: false});
    }

    // Impede números negativos
    if (value < 0) {
      control?.setValue(0, {emitEvent: false});
    }
  }

  onClickAjustarProcesso() {
    this.showInputMaskProcesso = true;
    this.messageService.add({
      severity: 'warn',
      summary: 'Atenção!',
      detail: 'Informe o número do processo SEI.'
    });
  }

  protected getMaskProcesso() {
    return '***.********/****-**';
  }

  private setEnums(): void {
    // Cópia nova de cada lista: o EnumsService preenche os arrays por mutação
    // (splice), então sem o spread o p-select não detecta a troca de referência.
    this.superintendenciaOptions = [...this.enumsService.superintendencias];
    this.diretoriaOptions = [...this.enumsService.diretorias];
    this.concessionariaOptions = [...this.enumsService.concessionarias];
    this.motivoOptions = [...this.enumsService.motivos];
    this.statusOptions = [...this.enumsService.status];
    this.faseOptions = [...this.enumsService.fases];
    this.tipoTapOptions = [...this.enumsService.tiposTap];
    this.situacaoTapOptions = [...this.enumsService.situacoesTap];
    this.cursoOptions = [...this.enumsService.cursos];
    this.grupoOptions = [...this.enumsService.grupos];
    this.nivelOptions = [...this.enumsService.niveis];
  }

  private brlToDecimal(value: string): string {
    if (!value) return '0';
    // remove R$, pontos de milhar e espaços
    const clean = value.replace(/[R$\s\.]/g, '');
    // substitui vírgula decimal por ponto
    return clean.replace(/,/g, '.');
  }

  private checkProcessoMask(processo: string | undefined) {
    if (!processo) {
      return true;
    }
    console.log('[AddEditNotificacao] verificando formatação do número de Processo.')
    const regexProcessoSEI = /\d{3}\.\d{8}\/\d{4}-\d{2}$/;
    const result = regexProcessoSEI.test(processo);
    console.log('[AddEditNotificacao] número de processo está no formato SEI? ', result)
    return result;
  }
}
